import { useRef } from "react";

import { clsxMerge } from "shared/lib/helpers";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";
import { useDynamicFontSize } from "shared/lib/hooks/use-dynamic-font-size";

interface DynamicTextSizePropsI extends PropsWithClassNameI {
  text?: string;
  initialFontSize?: number;
  minFontSize?: number;
}

/**
 * Dynamically adjust the font size of a text element to fit within a container.
 * Useful for cases where the text should stay on a single line and not wrap.
 * This component uses the useDynamicFontSize hook under the hood, and applies the necessary classNames to the elements for it to work.
 * @param className - The className to apply to the container.
 * @param text - The text to adjust the font size of.
 * @param initialFontSize - The initial font size of the text.
 * @param minFontSize - The minimum font size of the text.
 */
export const DynamicTextSize = ({
  className,
  text,
  initialFontSize = 24,
  minFontSize = 12,
}: DynamicTextSizePropsI) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);

  const fontSize = useDynamicFontSize({
    containerRef,
    textRef,
    initialFontSize,
    minFontSize,
  });

  return (
    <div
      ref={containerRef}
      className={clsxMerge("w-full overflow-hidden", className)}
    >
      <span
        ref={textRef}
        className={clsxMerge("inline-block whitespace-nowrap")}
        style={{ fontSize: `${fontSize}px` }}
      >
        {text}
      </span>
    </div>
  );
};
